import { useSuspenseCurrentMedication } from '@/hooks/useCurrentMedication'
import { buildMedicationOptions, productLevelMedicationFor } from '@/lib/medication-options'
import { isMpsCategoryMedication, isMpsMedRequest, isMpsPlaceholderProduct } from '@/lib/utils'
import { useGetSuspenseMedications } from '@/services/medications'
import { useCallback, useMemo } from 'react'

/** The current med request's medication catalogue, filtered and deduped for the combobox. */
export function useMedicationOptions() {
  const { data: medRequestData } = useSuspenseCurrentMedication()
  const { data: medications } = useGetSuspenseMedications()

  const isMps = isMpsMedRequest(medRequestData)

  // Before the cadence filter, so multi product order cards can filter per item.
  const catalogue = useMemo(
    () =>
      (medications ?? []).filter(
        medication =>
          // Never a treatment, not even as the ordered item.
          !isMpsPlaceholderProduct(medication.productName)
        // MPS stays in its category; the MPS section prepends an out-of-category pick.
        //  && (!isMps || isMpsCategoryMedication(medication))
      ),
    [medications]
  )

  const options = useMemo(
    () =>
      buildMedicationOptions({
        medications: catalogue,
        orderedItemId: medRequestData?.orderedItem?.id,
        requestFrequency: medRequestData?.subscriptionFrequency,
        orderNumber: medRequestData?.orderNumber,
        // MPS orders offer the full catalogue: their items carry no cadence
        ignoreCadence: isMps
      }),
    [
      catalogue,
      isMps,
      medRequestData?.subscriptionFrequency,
      medRequestData?.orderedItem?.id,
      medRequestData?.orderNumber
    ]
  )

  const findMedicationByHash = (lookupId: string) => medications.find(m => m.variantHash === lookupId)

  // Exact variant first; a product-only hash (PHP stored no variation) gets a product-level stand-in.
  const findMedication = useCallback(
    (lookupId: string) =>
      medications.find(m => m.variantHash === lookupId) ?? productLevelMedicationFor(medications, lookupId),
    [medications]
  )

  return { options, catalogue, medications, findMedicationByHash, findMedication }
}
